import { useMemo, useState } from 'react';
import { Document, Page } from 'react-pdf';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { Button, Modal } from '@tes/ui';
import { getToken } from '../lib/auth';
import { downloadWithAuth } from '../lib/download';
import { messageOf } from '../lib/errors';

const RENDER_WIDTH = 760;

/**
 * Read-only PDF viewer for a bearer-authenticated artifact route. react-pdf
 * fetches with the Authorization header (same pattern as the editor canvas),
 * so nothing is exposed on a plain URL.
 */
export function PdfPreviewModal({
  title,
  path,
  downloadName,
  onClose,
}: {
  title: string;
  path: string;
  downloadName: string;
  onClose: () => void;
}) {
  const file = useMemo(
    () => ({ url: path, httpHeaders: { Authorization: `Bearer ${getToken() ?? ''}` } }),
    [path],
  );
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      await downloadWithAuth(path, downloadName);
    } catch (e) {
      setError(messageOf(e, 'Download failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      size="xl"
      bodyClassName="bg-slate-100"
      footer={
        <>
          <div className="mr-auto flex items-center gap-1 text-sm text-slate-600">
            <button
              type="button"
              className="rounded p-1 hover:bg-slate-100 disabled:opacity-40"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              aria-label="Previous page"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="tabular-nums">
              {pages ? `Page ${page} / ${pages}` : '…'}
            </span>
            <button
              type="button"
              className="rounded p-1 hover:bg-slate-100 disabled:opacity-40"
              disabled={!pages || page >= pages}
              onClick={() => setPage((p) => p + 1)}
              aria-label="Next page"
            >
              <ChevronRight size={18} />
            </button>
          </div>
          <Button variant="secondary" onClick={() => void download()} disabled={busy}>
            <Download size={16} className="mr-1" />
            {busy ? 'Downloading…' : 'Download'}
          </Button>
        </>
      }
    >
      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      <div className="flex justify-center py-2">
        <Document
          file={file}
          onLoadSuccess={(d) => setPages(d.numPages)}
          onLoadError={(e) => setError(messageOf(e, 'Could not load the PDF'))}
          loading={<p className="p-8 text-sm text-slate-400">Loading…</p>}
          error={<p className="p-8 text-sm text-red-600">Could not load the PDF.</p>}
        >
          <Page
            pageNumber={page}
            width={RENDER_WIDTH}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            className="shadow ring-1 ring-slate-300"
          />
        </Document>
      </div>
    </Modal>
  );
}
